//src/components/layout/Header.jsx

"use client";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import {
  Search,
  ShoppingCart,
  User,
  X,
  LogOut,
  Heart,
  Loader,
  ChevronRight,
  Star,
  BadgeCheck,
  AlertCircle,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AnnouncementTicker } from "@/components/layout/AnnouncementTicker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { searchProducts } from "@/services/productService";
import { getDisplayPrice, formatPrice } from "@/services/pricingService";
import {
  getProductIdentifier,
  getProductTypeLabel,
  isOemProduct,
  isVehicleBasedProduct,
} from "@/utils/productType";
import { resolveProductImages } from "@/utils/media";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

import Image from "next/image";
import { logger } from "@/utils/logger";

const flattenCategoryHierarchy = (nodes = []) => {
  const flat = [];
  const stack = Array.isArray(nodes) ? [...nodes] : [];
  while (stack.length) {
    const node = stack.pop();
    if (!node) continue;
    flat.push(node);
    if (Array.isArray(node.children) && node.children.length) {
      node.children.forEach((child) => stack.push(child));
    }
  }
  return flat;
};

const getCategoryEntityId = (entity) => {
  if (!entity) return "";
  if (typeof entity === "string") return entity;
  if (typeof entity === "object") {
    if (entity._id) return String(entity._id);
    if (entity.id) return String(entity.id);
    if (entity.categoryCode) return String(entity.categoryCode);
  }
  return "";
};

const SearchResultsDropdown = ({
  results,
  isLoading,
  isVisible,
  onClose,
  query,
  user,
  categoryMatches = [],
}) => {
  if (!isVisible) return null;

  return (
    <div className="absolute top-full left-0 right-0 mt-3 bg-zinc-950/95 text-zinc-100 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-200">
      {isLoading ? (
        <div className="p-6 flex flex-col items-center justify-center text-zinc-400">
          <Loader className="animate-spin h-6 w-6 mb-2 text-primary" />
          <span className="text-sm font-medium">Searching...</span>
        </div>
      ) : results.length > 0 || categoryMatches.length > 0 ? (
        <div className="max-h-[65vh] overflow-y-auto scrollbar-hide">
          <div className="p-2 space-y-1">
            {categoryMatches.length > 0 && (
              <div className="rounded-2xl border border-amber-500/20 bg-amber-500/6 p-3 mb-2">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-400">
                      Category matches
                    </p>
                    <p className="text-sm font-semibold text-zinc-100">
                      Browse matching categories
                    </p>
                  </div>
                  <span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-[10px] font-semibold text-amber-400 shadow-sm">
                    {categoryMatches.length} match{categoryMatches.length === 1 ? "" : "es"}
                  </span>
                </div>
                {results.length === 0 && (
                  <p className="mb-2 text-[11px] text-zinc-400">
                    No product matches found yet. Browse matching categories below.
                  </p>
                )}

                <div className="grid gap-2 sm:grid-cols-2">
                  {categoryMatches.map((category) => (
                    <Link
                      key={category.key}
                      href={category.slug ? `/categories/${category.slug}` : "/categories"}
                      className="group flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 transition-colors hover:border-amber-500/40 hover:bg-white/10"
                      onClick={onClose}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-zinc-100">
                          {category.label}
                        </p>
                        <p className="text-[10px] text-zinc-400">
                          View all in this category
                        </p>
                      </div>
                      <div className="ml-2 shrink-0 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-400">
                        {category.count > 0
                          ? `${category.count} product${category.count === 1 ? "" : "s"}`
                          : "Open"}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {results.map((product) => {
              const { price, type, originalPrice } = getDisplayPrice(
                product,
                user,
              );
              const isWholesale = type === "wholesale";
              const inStock = product.stockStatus !== "OUT_OF_STOCK";
              const isOem = isOemProduct(product.productType);

              // Fallback for MRP/Original price logic
              const retailMrp = Number(
                product?.retailPrice?.mrp || product?.mrp || 0,
              );
              const showOriginalPrice =
                originalPrice || (retailMrp > price ? retailMrp : null);
              const imageSrc =
                resolveProductImages(product?.images || [])[0] ||
                "/placeholder.jpg";

              return (
                <Link
                  href={`/product/${product.slug}`}
                  key={product._id}
                  className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 group transition-all duration-200 border border-transparent hover:border-white/10"
                  onClick={onClose}
                >
                  <div className="relative h-20 w-20 shrink-0 rounded-lg overflow-hidden bg-white border border-border/60 shadow-sm group-hover:shadow-md group-hover:border-amber-500/40 transition-all">
                    <Image
                      src={imageSrc}
                      alt={product.name}
                      fill
                      sizes="80px"
                      className="object-contain p-2"
                    />
                  </div>

                  <div className="flex-1 min-w-0 flex flex-col justify-between h-full gap-1">
                    <div>
                      {/* Top Row: Badge + Rating */}
                      <div className="flex items-center justify-between mb-1">
                        <Badge
                          variant={isOem ? "default" : "secondary"}
                          className="text-[10px] font-medium px-2 py-0.5 shadow-sm h-5"
                        >
                          {isOem ? (
                            <>
                              <ShieldCheck className="w-3 h-3 mr-1" />
                              {getProductTypeLabel(product.productType)}
                            </>
                          ) : (
                            getProductTypeLabel(product.productType)
                          )}
                        </Badge>

                        <div className="flex items-center gap-1">
                          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                          <span className="text-xs font-medium text-zinc-400">
                            {Number(product.ratingAvg || 0).toFixed(1)}
                          </span>
                          <span className="text-[10px] text-zinc-400">
                            ({product.ratingCount || 0})
                          </span>
                        </div>
                      </div>

                      {/* Product Name */}
                      <p className="font-semibold text-sm line-clamp-1 text-zinc-100 group-hover:text-amber-400 transition-colors">
                        {product.name}
                      </p>

                      {/* Details: Brand â€¢ Part Number */}
                      <div className="flex items-center gap-2 text-xs text-zinc-400 mt-0.5">
                        <span className="truncate max-w-[100px]">
                          {isVehicleBasedProduct(product.productType)
                            ? product.vehicleBrand ||
                              product.manufacturerBrand ||
                              "N/A"
                            : product.manufacturerBrand || "N/A"}
                        </span>
                        <span>â€¢</span>
                        <span className="font-mono bg-white/10 px-1 rounded">
                          {getProductIdentifier(product)}
                        </span>
                      </div>

                      {/* Category */}
                      {product.categorySlug && (
                        <div className="text-[10px] text-zinc-400 mt-1 truncate opacity-80">
                          in{" "}
                          <span className="font-medium">
                            {product.categorySlug.replace(/-/g, " ")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Side: Price + Stock */}
                  <div className="text-right shrink-0 flex flex-col items-end justify-between h-full gap-2 self-stretch pt-1">
                    <div className="flex flex-col items-end">
                      <span className="font-bold text-base text-primary">
                        {formatPrice(price)}
                      </span>

                      {showOriginalPrice && showOriginalPrice > price && (
                        <span className="text-xs text-zinc-400 line-through decoration-destructive/50">
                          {formatPrice(showOriginalPrice)}
                        </span>
                      )}

                      {isWholesale && (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1 h-5 mt-1 border-primary/20 text-primary bg-primary/5"
                        >
                          Wholesale
                        </Badge>
                      )}
                    </div>

                    <span
                      className={cn(
                        "flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border mb-1",
                        inStock
                          ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
                          : "text-red-400 border-red-500/30 bg-red-500/10",
                      )}
                    >
                      {inStock ? (
                        <BadgeCheck className="h-3 w-3" />
                      ) : (
                        <AlertCircle className="h-3 w-3" />
                      )}
                      {inStock ? "In Stock" : "Out of Stock"}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
          <Link
            href={`/search?q=${encodeURIComponent(query)}`}
            className="flex items-center justify-between px-4 py-3 bg-white/5 hover:bg-white/10 border-t border-white/10 text-sm font-medium text-amber-400 transition-colors cursor-pointer"
            onClick={onClose}
          >
            <span>
              {results.length > 0
                ? `View all items for "${query}"`
                : categoryMatches.length > 0
                  ? `View all search results for "${query}"`
                  : `Browse all results for "${query}"`}
            </span>
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      ) : (
        <div className="p-8 text-center">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 mb-3">
            <Search className="h-5 w-5 text-zinc-400" />
          </div>
          <p className="text-sm font-medium text-zinc-100">
            No matches found
          </p>
          <p className="text-xs text-zinc-400 mt-1">
            Try checking your spelling or browse categories.
          </p>
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [hasMounted, setHasMounted] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  // Search State
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [categoryCatalog, setCategoryCatalog] = useState([]);
  const searchRef = useRef(null);

  const { itemCount } = useCart();
  const { itemCount: wishlistCount } = useWishlist();
  const { user, isAuthenticated, logout } = useAuth();
  const { siteName } = useSiteConfig();
  const router = useRouter();
  const searchParams = useSearchParams();

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const ensureCategoryCatalog = useCallback(async () => {
    if (categoryCatalog.length > 0) return;
    try {
      const mod = await import("@/services/categoryService");
      const hierarchy = await mod.getCategories();
      setCategoryCatalog(flattenCategoryHierarchy(hierarchy));
    } catch (_error) {
      setCategoryCatalog([]);
    }
  }, [categoryCatalog]);

  const isAuthReady = hasMounted && isAuthenticated;
  const authUser = isAuthReady ? user : null;

  // Handle Focus Search Intent from Mobile Bottom Nav
  useEffect(() => {
    const handleOpenSearch = () => {
      setIsSearchOpen(true);
      // Optional: focus the input if possible, but state change usually triggers render which shows the input
    };

    window.addEventListener("open-mobile-search", handleOpenSearch);

    // Also keep the param check as valid fallback/direct link support
    if (searchParams?.get("focus") === "search") {
      setIsSearchOpen(true);
    }

    return () => {
      window.removeEventListener("open-mobile-search", handleOpenSearch);
    };
  }, [searchParams]);

  // Handle Search Fetching
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchQuery.trim().length > 1) {
        setIsSearching(true);
        setShowResults(true);
        try {
          // Pass limit: 6 for a quick preview
          const results = await searchProducts(searchQuery, { limit: 6 });
          setSearchResults(results);
        } catch (error) {
          logger.error("Search error", error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  // Handle Click Outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the search container
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowResults(false);
      router.push(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleLogout = () => {
    logout();
    setShowLogoutDialog(false);
    toast.success("Logged out successfully");
    router.push("/");
  };

  const displayName =
    isAuthReady && authUser
      ? `${authUser.firstName ?? ""} ${authUser.lastName ?? ""}`.trim() ||
        authUser.email ||
        "User"
      : "";

  const searchCategoryMatches = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query || categoryCatalog.length === 0) return [];
    const tokens = query.split(/\s+/).filter(Boolean);
    return categoryCatalog
      .map((entity) => {
        const label = String(entity?.name || entity?.slug || "").trim();
        const slug = String(entity?.slug || "");
        const key = getCategoryEntityId(entity) || slug || label;
        return {
          key,
          label,
          slug,
          count: 0,
        };
      })
      .filter((item) => {
        const label = item.label.toLowerCase();
        const slug = item.slug.toLowerCase();
        return (
          label.includes(query) ||
          slug.includes(query) ||
          tokens.some((token) => token && (label.includes(token) || slug.includes(token)))
        );
      })
      .slice(0, 4);
  }, [searchQuery, categoryCatalog]);

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b border-white/10 bg-[#0b0b10]/95 text-white backdrop-blur-md supports-[backdrop-filter]:bg-[#0b0b10]/85 shadow-lg shadow-black/20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex h-20 items-center justify-between gap-4">
            {/* Logo */}
            <Link
              href="/"
              className="flex items-center gap-2 shrink-0 transition-opacity hover:opacity-90"
            >
              <Image
                src="/logo-emblem.png"
                alt={`${siteName || "Car Spares"} logo`}
                width={40}
                height={40}
                className="h-9 w-9 md:h-10 md:w-10 rounded-full"
                priority
              />
              <span className="text-base md:text-lg font-extrabold tracking-[0.14em] text-white">
                CAR SPARES
              </span>
            </Link>

            {/* Desktop Search Bar */}
            <div
              className="hidden md:flex flex-1 items-center justify-center max-w-2xl px-8"
              ref={searchRef}
            >
              <form onSubmit={handleSearch} className="w-full relative group">
                <div className="relative z-50">
                 
                  <Input
                    type="search"
                    placeholder="Search parts, brands, OEM numbers..."
                    aria-label="Search spare parts"
                    value={searchQuery}
                    onChange={(e) => {
                      const next = e.target.value;
                      setSearchQuery(next);
                      if (next.trim().length > 1) ensureCategoryCatalog();
                    }}
                    onFocus={() => {
                      ensureCategoryCatalog();
                      searchQuery.length > 1 && setShowResults(true);
                    }}
                    className="w-full pl-12 pr-4 h-11 bg-white/10 hover:bg-white/15 focus:bg-white/15 border-transparent focus:border-amber-500/40 rounded-full text-white shadow-sm focus:shadow-md transition-all duration-300 placeholder:text-zinc-400"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden group-focus-within:block">
                    <span className="text-xs text-zinc-400 bg-white/10 px-2 py-1 rounded-md">
                      Enter
                    </span>
                  </div>
                </div>
                {/* Desktop Dropdown */}
                <SearchResultsDropdown
                  results={searchResults}
                  isLoading={isSearching}
                  isVisible={showResults}
                  onClose={() => setShowResults(false)}
                  query={searchQuery}
                  user={user}
                  categoryMatches={searchCategoryMatches}
                />
              </form>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-1.5 sm:gap-2 max-[420px]:gap-1">
              {/* Mobile Search Toggle */}
              <Button
                variant="ghost"
                size="icon"
                aria-label="Toggle mobile search bar"
                className="md:hidden h-9 w-9 rounded-full hover:bg-white/10 text-zinc-300"
                onClick={() => setIsSearchOpen(!isSearchOpen)}
              >
                {isSearchOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Search className="h-5 w-5" />
                )}
              </Button>

              {/* Wishlist */}
              <Link
                href="/wishlist"
                aria-label={
                  wishlistCount > 0
                    ? `View wishlist, ${wishlistCount} ${wishlistCount === 1 ? "item" : "items"}`
                    : "View wishlist, empty"
                }
              >
                <Button
                  variant="ghost"
                  size="icon"
                  tabIndex={-1}
                  aria-hidden="true"
                  className="relative h-9 w-9 rounded-full hover:bg-white/10 text-zinc-300 hover:text-amber-400 transition-colors"
                >
                  <Heart className="h-5 w-5 md:h-5 md:w-5" />
                  {wishlistCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 h-4 w-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-zinc-950 animate-in zoom-in-50 duration-300">
                      {wishlistCount > 99 ? "99+" : wishlistCount}
                    </span>
                  )}
                </Button>
              </Link>

              {/* Cart */}
              <Link
                href="/cart"
                aria-label={
                  itemCount > 0
                    ? `View shopping cart, ${itemCount} ${itemCount === 1 ? "item" : "items"}`
                    : "View shopping cart, empty"
                }
              >
                <Button
                  variant="ghost"
                  size="icon"
                  tabIndex={-1}
                  aria-hidden="true"
                  className="relative h-9 w-9 rounded-full hover:bg-white/10 text-zinc-300 hover:text-amber-400 transition-colors"
                >
                  <ShoppingCart className="h-5 w-5 md:h-6 md:w-6" />
                  {itemCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 h-4 w-4 bg-primary text-primary-foreground text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-zinc-950 animate-in zoom-in-50 duration-300">
                      {itemCount > 99 ? "99+" : itemCount}
                    </span>
                  )}
                </Button>
              </Link>

              {/* Auth: User Menu */}
              {isAuthReady ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="User account menu"
                      className="relative h-9 w-9 rounded-full hover:bg-white/10 text-zinc-300 hover:text-amber-400 transition-colors focus-visible:ring-2 focus-visible:ring-amber-500"
                    >
                      <User className="h-5 w-5 md:h-6 md:w-6" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-64 p-2 border-border/50 shadow-xl bg-background/95 backdrop-blur-md rounded-xl mt-2"
                  >
                    <div className="px-3 py-2.5 bg-secondary/30 rounded-lg mb-2">
                      <p className="text-sm font-semibold truncate">
                        {displayName}
                      </p>
                      <p className="text-xs text-zinc-400 truncate opacity-80">
                        {authUser?.email || authUser?.phone}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <DropdownMenuItem
                        asChild
                        className="rounded-lg cursor-pointer focus:bg-primary/10 focus:text-primary"
                      >
                        <Link
                          href="/profile"
                          className="flex items-center w-full"
                        >
                          My Profile
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        asChild
                        className="rounded-lg cursor-pointer focus:bg-primary/10 focus:text-primary"
                      >
                        <Link
                          href="/orders"
                          className="flex items-center w-full"
                        >
                          My Orders
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        asChild
                        className="rounded-lg cursor-pointer focus:bg-primary/10 focus:text-primary"
                      >
                        <Link
                          href="/wishlist"
                          className="flex items-center w-full"
                        >
                          My Wishlist
                        </Link>
                      </DropdownMenuItem>
                    </div>

                    <DropdownMenuSeparator className="my-2 bg-border/50" />

                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive focus:bg-destructive/10 cursor-pointer rounded-lg"
                      onClick={() => setShowLogoutDialog(true)}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link href="/login" className="ml-2">
                  <Button
                    size="sm"
                    className="rounded-full px-3 sm:px-6 font-medium shadow-md hover:shadow-lg transition-all duration-300 bg-primary hover:bg-primary/90 h-9 sm:h-10 max-[375px]:px-2 max-[375px]:w-9"
                  >
                    <User className="w-4 h-4 sm:mr-2" />
                    <span className="max-[375px]:hidden">Login</span>
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Mobile Search Bar - Expandable */}
          <div
            className={cn(
              "md:hidden transition-all duration-300 ease-in-out origin-top",
              isSearchOpen
                ? "max-h-screen opacity-100 mb-4 overflow-visible"
                : "max-h-0 opacity-0 overflow-hidden",
            )}
          >
            <form onSubmit={handleSearch} className="pb-2 relative">
              <div className="relative">
             
                <Input
                  type="search"
                  placeholder="Search parts, brands, OEM numbers..."
                  aria-label="Search spare parts"
                  value={searchQuery}
                  onChange={(e) => {
                    const next = e.target.value;
                    setSearchQuery(next);
                    if (next.trim().length > 1) ensureCategoryCatalog();
                  }}
                  onFocus={() => {
                    ensureCategoryCatalog();
                    searchQuery.length > 1 && setShowResults(true);
                  }}
                  className="w-full pl-14 pr-4 h-12 bg-white/10 border-transparent rounded-full focus:bg-white/15 focus:border-amber-500/40 text-white shadow-sm transition-all placeholder:text-zinc-400"
                  autoFocus={isSearchOpen}
                />
              </div>
              {/* Mobile Dropdown - Rendered relatively within the expanded zone */}
              <div className="mt-2">
                <SearchResultsDropdown
                  results={searchResults}
                  isLoading={isSearching}
                  isVisible={showResults}
                  onClose={() => setShowResults(false)}
                  query={searchQuery}
                  user={user}
                  categoryMatches={searchCategoryMatches}
                />
              </div>
            </form>
          </div>
        </div>
      </header>

      {/* Scrolling announcements — edit copy in app.config.js */}
      <AnnouncementTicker />

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent className="rounded-2xl border border-border shadow-2xl bg-background/95 backdrop-blur-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl">Logout?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to logout from your account?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl border-border/50 hover:bg-secondary/80">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLogout}
              className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-md"
            >
              Yes, Logout
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Header;
